import React, { useEffect } from "react";
import { WidgetWrapper } from "../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../components/EditPanel/types";
import { TextBoxProps, TextBoxState } from "./types";
import { useWidgetEditable } from "../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../utils/fontWeightOptions";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: TextBoxState = {
  content: "Default Text",
  fontFamily: "inherit",
  fontSize: 16,
  fontWeight: "medium",
  color: {
    type: "theme",
    value: "textPrimary",
  },
  textAlign: "left",
  lineHeight: 1.5,
  letterSpacing: 0,
  textTransform: "none",
};

const editFields: EditField[] = [
  { key: "content", label: "Text Content", type: "text" },
  { key: "fontFamily", label: "Font Family", type: "font" },
  {
    key: "fontSize",
    label: "Font Size (px)",
    type: "number",
    min: 12,
    max: 72,
    step: 1,
  },
  {
    key: "fontWeight",
    label: "Font Weight",
    type: "options",
    options: FONT_WEIGHT_OPTIONS_KEYWORDS,
  },
  { key: "color", label: "Text Color", type: "color" },
  {
    key: "textAlign",
    label: "Text Alignment",
    type: "options",
    options: [
      { label: "Left", value: "left" },
      { label: "Center", value: "center" },
      { label: "Right", value: "right" },
    ],
  },
  {
    key: "lineHeight",
    label: "Line Height",
    type: "number",
    min: 1.0,
    max: 3.0,
    step: 0.1,
  },
  {
    key: "letterSpacing",
    label: "Letter Spacing (px)",
    type: "number",
    min: -2,
    max: 5,
    step: 0.5,
  },
  {
    key: "textTransform",
    label: "Text Transform",
    type: "options",
    options: [
      { label: "None", value: "none" },
      { label: "Uppercase", value: "uppercase" },
      { label: "Lowercase", value: "lowercase" },
      { label: "Capitalize", value: "capitalize" },
    ],
  },
];

export const TextBox: React.FC<TextBoxProps> = ({
  initialState = {},
  widgetId,
  displayName,
  excludeEditFields,
}) => {
  const effectiveEditFields = excludeEditFields
    ? editFields.filter((f) => !excludeEditFields.includes(f.key))
    : editFields;

  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<TextBoxState>(
    widgetId,
    { ...defaultState, ...initialState },
    effectiveEditFields,
  );

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.fontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  // (not inline, because Shadow DOM isolates @font-face declarations)
  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  // Load Google Font into document.head when font changes
  // NOTE: This hook must be called before any early returns to satisfy React's rules of hooks
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Map font weight to CSS numeric values
  const getFontWeightValue = (weight: string): number => {
    const weightMap: Record<string, number> = {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    };
    // If it's already a numeric string (800, 900), parse it
    const numericWeight = parseInt(weight, 10);
    if (!isNaN(numericWeight)) {
      return numericWeight;
    }
    return weightMap[weight] || 500;
  };

  // Determine font-family CSS based on font type
  // - system: use the url field which contains the CSS font-family value
  // - google/upload: use the font name
  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url // System fonts store CSS font-family in url field
      : `"${fontData.name}", sans-serif`
    : "inherit";

  // Generate unique class for this widget instance (stable based on widgetId)
  const uniqueClass = `textbox-${widgetId || "default"}`;

  // Determine if we need @font-face (only for uploaded fonts with actual file URLs)
  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  // Generate @font-face CSS only for uploaded fonts
  // Also include !important override rule for font-family
  const styleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Render the actual widget content
  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      <span
        className={`textbox-content ${uniqueClass}`}
        style={{
          // font-family is set via CSS class with !important, only use inline for inherit
          ...(fontFamilyCss === "inherit" ? { fontFamily: "inherit" } : {}),
          fontSize: `${state.fontSize}px`,
          fontWeight: getFontWeightValue(state.fontWeight),
          color: resolveColor(state.color),
          textAlign: state.textAlign,
          lineHeight: state.lineHeight,
          letterSpacing: `${state.letterSpacing}px`,
          textTransform: state.textTransform,
          display: "inline-block",
          width: "100%",
        }}
      >
        {state.content}
      </span>
    </>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={effectiveEditFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
